import { Dispatch, FC, RefObject, SetStateAction, useState } from "react";
import { dollars } from "./dollars";
import BottomSheet from "./BottomSheet";
import ProductDetails, { deductibleLine, detailLines, Line, plainLine } from "./ProductDetails";
import ProofUpload, { ProofFile } from "./ProofUpload";
import ShowMore from "./ShowMore";

export const per = (nature?: string) =>
  nature === "per_trip" ? "trip" : nature === "per_day" ? "day" : "24 hours";

/**
 * Only a Bonzah package has coverage to read in "View coverage" (its terms, brochure and Bonzah's links). The
 * company's own options (Rental Cover, Allianz, "I have my own insurance") show their note on the card and
 * have nothing more behind a link (1NOW-11384).
 */
export const hasCoverageToView = (p: any): boolean => p?.insurance_by_bonzah === true;

/** A package that costs nothing ($0, "0.00", no amount) shows no price label at all; it is still an option (1NOW-11384). */
export const showsPrice = (p: any): boolean => Number(p?.price) !== 0;

/**
 * The packages in the order the renter sees them: "I have my own" is always first (1NOW-11384), then the company's
 * options and Bonzah's in the order the API sent them. Only the order: which package is chosen at first and every
 * price are the page's, and a chosen package is looked up by its value, not its place.
 */
export const orderProducts = (products: any[] | undefined): any[] => {
  const own = (p: any) => p?.canopy === true || p?.upload_before_payment === true;
  const list = products ?? [];
  return [...list.filter(own), ...list.filter((p) => !own(p))];
};

/**
 * Step 2 of the phone booking: protection as radio cards (1NOW-11155). It
 * draws the same packages the desktop section lists and selects through the
 * page's own handler (`onSelect` = attemptBonzahProductSelection), so the
 * Bonzah terms popup, the >30-day rules and the proof-of-insurance rule are
 * the page's, not a copy.
 */
const ProtectionStep: FC<{
  products: any[];
  loading: boolean;
  notice?: string;
  selectedValue: any;
  onSelect: (product: any) => void;
  bgColor?: string;
  proof: {
    files: ProofFile[];
    setFiles: Dispatch<SetStateAction<ProofFile[]>>;
    inputRef: RefObject<HTMLInputElement>;
  };
}> = ({ products, loading, notice, selectedValue, onSelect, bgColor, proof }) => {
  // "View coverage" opens the package's details in a sheet, as Sixt does (Taha, 6 Oct).
  const [viewing, setViewing] = useState<any | null>(null);
  const list = orderProducts(products);
  const isSelected = (p: any) => JSON.stringify(selectedValue) === JSON.stringify(p?.value);

  if (!loading && list.length === 0)
    return (
      <>
        {notice && <p className="mb-3 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{notice}</p>}
        <p className="rounded-2xl border border-secondary-100 p-4 text-sm">No protection options are offered for this trip.</p>
      </>
    );

  return (
    <div role="radiogroup" aria-label="Protection" className="flex flex-col gap-3">
      {notice && <p className="rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{notice}</p>}
      {list.map((p) => {
        const on = isSelected(p);
        const ded = deductibleLine(p.options);
        // No View coverage on a company's own option, so its note goes on the card (as the desktop row prints it).
        const note = hasCoverageToView(p) ? [] : detailLines(p.options).filter((l) => l !== ded && plainLine(l));
        return (
          <div
            key={p.title}
            className={`rounded-2xl border-2 bg-card-color p-3 ${on ? "border-text-color" : "border-secondary-100"}`}
          >
            <div className="flex items-start gap-3">
              <button
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => onSelect(p)}
                className="flex min-w-0 flex-1 items-start gap-3 text-left"
              >
                <span
                  aria-hidden="true"
                  className={`mt-0.5 h-5 w-5 shrink-0 rounded-full ${on ? "border-[6px] border-text-color" : "border-2 border-secondary-100"}`}
                />
                <span className="min-w-0 flex-1">
                  <span className="block break-words text-base font-extrabold">{p.title}</span>
                  {ded && <span className="block text-xs font-bold text-green-700">{ded}</span>}
                  {showsPrice(p) && (
                    <span className="mt-1 block text-lg font-extrabold">
                      {dollars(p.price)}
                      <small className="text-xs font-semibold"> / {per(p.nature)}</small>
                    </span>
                  )}
                </span>
              </button>
            </div>
            {note.length > 0 && (
              <ShowMore
                className="ml-8 mt-1 break-words text-sm text-text-color/70 [&>span]:block [&>span+span]:mt-1"
                linkClassName="ml-8 mt-1 min-h-[32px]"
              >
                {note.map((l, i) => (
                  <Line key={i} text={l} bgColor={bgColor} />
                ))}
              </ShowMore>
            )}
            {hasCoverageToView(p) && (
              <button
                type="button"
                onClick={() => setViewing(p)}
                aria-label={`View coverage: ${p.title}`}
                className="ml-8 mt-1 min-h-[32px] text-sm font-bold underline underline-offset-2"
              >
                View coverage
              </button>
            )}
            {on && p.upload_before_payment === true && (
              <div className="ml-8">
                <ProofUpload files={proof.files} setFiles={proof.setFiles} inputRef={proof.inputRef} />
              </div>
            )}
          </div>
        );
      })}
      <BottomSheet open={viewing !== null} onClose={() => setViewing(null)} title={viewing?.title ?? "Coverage"}>
        {viewing && <ProductDetails product={viewing} bgColor={bgColor} />}
      </BottomSheet>
    </div>
  );
};

export default ProtectionStep;
